import * as React from 'react';
import styled, { keyframes } from 'styled-components';

interface AppStoreModalProps {
	isOpen: boolean;
	onClose: () => void;
	onIOSClick: () => void;
	onAndroidClick: () => void;
}

const AppleIcon = () => (
	<svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
		<path d="M18.71 19.5C17.88 20.74 17 21.95 15.66 21.97C14.32 22 13.89 21.18 12.37 21.18C10.84 21.18 10.37 21.95 9.1 22C7.79 22.05 6.8 20.68 5.96 19.47C4.25 16.95 2.93 12.45 4.7 9.39C5.57 7.87 7.13 6.91 8.82 6.88C10.1 6.86 11.32 7.75 12.11 7.75C12.89 7.75 14.37 6.68 15.92 6.84C16.57 6.87 18.39 7.1 19.56 8.82C19.47 8.88 17.39 10.1 17.41 12.63C17.44 15.65 20.06 16.66 20.09 16.67C20.06 16.74 19.67 18.11 18.71 19.5ZM13 3.5C13.73 2.67 14.94 2.04 15.94 2C16.07 3.17 15.6 4.35 14.9 5.19C14.21 6.04 13.07 6.7 11.95 6.61C11.8 5.46 12.36 4.26 13 3.5Z" />
	</svg>
);

const PlayStoreIcon = () => (
	<svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
		<path d="M3.609 1.814L13.792 12 3.61 22.186a.996.996 0 0 1-.61-.92V2.734a1 1 0 0 1 .609-.92zm10.89 10.893l2.302 2.302-10.937 6.333 8.635-8.635zm3.199-3.199l2.307 1.335c.8.46.8 1.617 0 2.076l-2.305 1.332L15.206 12l2.492-2.492zM5.864 2.658L16.802 8.99l-2.303 2.303-8.635-8.635z" />
	</svg>
);

const CloseIcon = () => (
	<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" aria-hidden="true">
		<path d="M18 6L6 18M6 6l12 12" />
	</svg>
);

const ChevronIcon = () => (
	<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
		<path d="M9 18l6-6-6-6" />
	</svg>
);

export const AppStoreModal: React.FC<AppStoreModalProps> = ({ isOpen, onClose, onIOSClick, onAndroidClick }) => {
	React.useEffect(() => {
		if (!isOpen) return;

		const onKeyDown = (e: KeyboardEvent) => {
			if (e.key === 'Escape') onClose();
		};

		document.body.style.overflow = 'hidden';
		window.addEventListener('keydown', onKeyDown);
		return () => {
			document.body.style.overflow = '';
			window.removeEventListener('keydown', onKeyDown);
		};
	}, [isOpen, onClose]);

	if (!isOpen) return null;

	return (
		<Overlay onClick={onClose} role="presentation">
			<ModalContent onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby="app-store-modal-title">
				<CloseButton onClick={onClose} aria-label="Close dialog" type="button">
					<CloseIcon />
				</CloseButton>

				<Header>
					<Title id="app-store-modal-title">Download Live Rewards</Title>
					<Subtitle>Choose your platform to get started</Subtitle>
				</Header>

				<ButtonContainer>
					<StoreButton type="button" onClick={onIOSClick} $accent="#111111">
						<IconBadge $bg="#111111">
							<AppleIcon />
						</IconBadge>
						<StoreCopy>
							<StoreTitle>Get on iOS</StoreTitle>
							<StoreMeta>Apple App Store</StoreMeta>
						</StoreCopy>
						<Chevron>
							<ChevronIcon />
						</Chevron>
					</StoreButton>

					<StoreButton type="button" onClick={onAndroidClick} $accent="#027dfd">
						<IconBadge $bg="linear-gradient(135deg, #34a853 0%, #4285f4 45%, #ea4335 100%)">
							<PlayStoreIcon />
						</IconBadge>
						<StoreCopy>
							<StoreTitle>Get on Android</StoreTitle>
							<StoreMeta>Google Play Store</StoreMeta>
						</StoreCopy>
						<Chevron>
							<ChevronIcon />
						</Chevron>
					</StoreButton>
				</ButtonContainer>
			</ModalContent>
		</Overlay>
	);
};

const fadeIn = keyframes`
	from { opacity: 0; }
	to { opacity: 1; }
`;

const scaleIn = keyframes`
	from {
		opacity: 0;
		transform: translateY(12px) scale(0.97);
	}
	to {
		opacity: 1;
		transform: translateY(0) scale(1);
	}
`;

const Overlay = styled.div`
	position: fixed;
	inset: 0;
	background-color: rgba(15, 23, 42, 0.55);
	backdrop-filter: blur(6px);
	-webkit-backdrop-filter: blur(6px);
	display: flex;
	align-items: center;
	justify-content: center;
	z-index: 1000;
	padding: 20px;
	animation: ${fadeIn} 0.18s ease-out;
`;

const ModalContent = styled.div`
	background: #ffffff;
	border-radius: 20px;
	padding: 28px 28px 24px;
	max-width: 440px;
	width: 100%;
	box-shadow:
		0 24px 48px rgba(15, 23, 42, 0.18),
		0 0 0 1px rgba(15, 23, 42, 0.04);
	position: relative;
	animation: ${scaleIn} 0.22s cubic-bezier(0.16, 1, 0.3, 1);
`;

const CloseButton = styled.button`
	position: absolute;
	top: 14px;
	right: 14px;
	width: 36px;
	height: 36px;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	background: #f4f5f7;
	border: none;
	border-radius: 50%;
	cursor: pointer;
	color: #64748b;
	transition:
		background 0.15s ease,
		color 0.15s ease,
		transform 0.15s ease;

	&:hover {
		background: #e8eaed;
		color: #0f172a;
	}

	&:active {
		transform: scale(0.96);
	}
`;

const Header = styled.div`
	padding-right: 36px;
	margin-bottom: 24px;
`;

const Title = styled.h2`
	margin: 0;
	font-size: 26px;
	line-height: 1.2;
	font-weight: 800;
	color: #0f172a;
	letter-spacing: -0.02em;
`;

const Subtitle = styled.p`
	margin: 8px 0 0;
	font-size: 15px;
	line-height: 1.45;
	color: #64748b;
	font-weight: 500;
`;

const ButtonContainer = styled.div`
	display: flex;
	flex-direction: column;
	gap: 12px;
`;

const StoreButton = styled.button<{ $accent: string }>`
	display: flex;
	align-items: center;
	gap: 14px;
	width: 100%;
	background: #f8fafc;
	border: 1.5px solid #e2e8f0;
	border-radius: 14px;
	padding: 14px 16px;
	cursor: pointer;
	transition:
		border-color 0.18s ease,
		background 0.18s ease,
		box-shadow 0.18s ease,
		transform 0.18s ease;
	text-align: left;

	&:hover {
		border-color: ${({ $accent }) => $accent};
		background: #ffffff;
		box-shadow: 0 8px 24px rgba(15, 23, 42, 0.08);
		transform: translateY(-1px);

		> :last-child {
			color: ${({ $accent }) => $accent};
			transform: translateX(2px);
		}
	}

	&:active {
		transform: translateY(0);
		box-shadow: none;
	}

	&:focus-visible {
		outline: 2px solid #027dfd;
		outline-offset: 2px;
	}
`;

const IconBadge = styled.div<{ $bg: string }>`
	flex-shrink: 0;
	width: 48px;
	height: 48px;
	border-radius: 12px;
	display: flex;
	align-items: center;
	justify-content: center;
	background: ${({ $bg }) => $bg};
	color: #ffffff;
	box-shadow: 0 4px 12px rgba(15, 23, 42, 0.12);
`;

const StoreCopy = styled.div`
	display: flex;
	flex-direction: column;
	gap: 2px;
	min-width: 0;
	flex: 1;
`;

const StoreTitle = styled.span`
	font-size: 16px;
	font-weight: 700;
	color: #0f172a;
	letter-spacing: -0.01em;
`;

const StoreMeta = styled.span`
	font-size: 13px;
	font-weight: 500;
	color: #64748b;
`;

const Chevron = styled.span`
	flex-shrink: 0;
	color: #94a3b8;
	display: inline-flex;
	transition:
		color 0.18s ease,
		transform 0.18s ease;
`;
